import { Customer } from "./customerTypes";

export interface CustomerExportFilters {
  province: string[];
  city: string;
  startDate: string;
  endDate: string;
}

export interface CustomerExportRequest {
  searchQuery: string;
  filters: CustomerExportFilters;
}

export const CUSTOMER_EXPORT_BATCH_SIZE = 1000;

const CUSTOMER_EXPORT_HEADERS = [
  "Customer ID",
  "Name",
  "Account Status",
  "Email",
  "Store count",
  "Phone number",
  "Address",
  "Date registered",
  "City",
  "Province",
  "Postal Code",
];

export const buildCustomerExportQuery = ({
  page,
  size = CUSTOMER_EXPORT_BATCH_SIZE,
  searchQuery,
  filters,
}: CustomerExportRequest & { page: number; size?: number }): string => {
  const params = new URLSearchParams();
  params.set("Page", page.toString());
  params.set("Size", size.toString());

  if (searchQuery) params.set("SearchQuery", searchQuery);
  filters.province.forEach((province) => params.append("Province", province));
  if (filters.city) params.set("City", filters.city);
  if (filters.startDate) params.set("StartDate", filters.startDate);
  if (filters.endDate) params.set("EndDate", filters.endDate);

  return `?${params.toString()}`;
};

export const hasActiveCustomerExportFilters = ({
  searchQuery,
  filters,
}: CustomerExportRequest): boolean => {
  return Boolean(
    searchQuery ||
      filters.city ||
      filters.startDate ||
      filters.endDate ||
      filters.province.length,
  );
};

const normalizeCell = (value: any): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const escapeCsvCell = (value: any): string => {
  const cell = normalizeCell(value);

  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }

  return cell;
};

export const formatCustomerExportDate = (value: any): string => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

export const getCustomerExportFileName = (date = new Date()): string => {
  return `mamimarkets_customers_${date.toISOString().slice(0, 10)}.csv`;
};

export const getCustomerExportHeaderRow = (): string => {
  return CUSTOMER_EXPORT_HEADERS.map(escapeCsvCell).join(",");
};

export const mapCustomerToExportRow = (customer: Customer & any): string => {
  const name = [customer?.firstName, customer?.lastName]
    .map(normalizeCell)
    .filter(Boolean)
    .join(" ");

  const row = [
    customer?.id,
    name,
    customer?.status,
    customer?.email,
    customer?.storeCount,
    customer?.phoneNumber,
    customer?.address,
    formatCustomerExportDate(customer?.createdDate),
    customer?.city,
    customer?.province,
    customer?.postCode,
  ];

  return row.map(escapeCsvCell).join(",");
};

export const downloadCustomerCsv = (csvContent: string, fileName: string): void => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
